import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {DateTime} from 'luxon';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown} from '@fortawesome/pro-solid-svg-icons';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import {SectionTriangle, TriangleBottom} from 'components/Elements/Section';
import Laurels from 'components/Laurels';
import FilmOptinForm from 'components/Forms/FilmOptin';
import FilmOptinModal, {FilmOptinModalTrigger} from 'components/Modals/FilmOptinModal';
import Video from 'components/Video';
import CountdownTimer from 'components/Elements/CountdownTimer';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';

import {criticsLanding, reviews, discover, discoverAd} from 'data/fungi.json';
import orids from 'data/orids.json';
import ReactMarkdown from 'react-markdown';

export default function LandingIndex({
  noExitIntent = false,
  noFixedVideo = true,
  noShareButtons = false,
  videoUrlOverride = null,
  variant = 'default',
  ...props
}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: ''
      },
      special: specialSchedule = {}
    }
  } = usePhaseState();

  const phase = specialSchedule.phase ? specialSchedule.phase : defaultSchedule.phase;
  const [partnerName, partnerLogo] = (specialSchedule.orid && orids[specialSchedule.orid]) || [];

  const toOrdinalSuffix = (num) => {
    const int = parseInt(num),
      digits = [int % 10, int % 100],
      ordinals = ['st', 'nd', 'rd', 'th'],
      oPattern = [1, 2, 3, 4],
      tPattern = [11, 12, 13, 14, 15, 16, 17, 18, 19];
    return oPattern.includes(digits[0]) && !tPattern.includes(digits[1])
      ? int + ordinals[digits[0] - 1]
      : int + ordinals[3];
  };

  const launchStart = specialSchedule.startTime
    ? DateTime.fromMillis(specialSchedule.startTime)
    : DateTime.fromISO(defaultSchedule.launchStart);
  const launchStartNum = launchStart.setZone('America/Los_Angeles').toFormat('d');

  const launchEnd = specialSchedule.endTime
    ? DateTime.fromMillis(specialSchedule.endTime)
    : DateTime.fromISO(defaultSchedule.launchEnd);
  const launchEndNum = launchEnd.setZone('America/Los_Angeles').toFormat('d');

  function Timer({className = null, headingClass = null, ...props}) {
    return (
      <>
        {phase === 'during' ? (
          <>
            <div className={classNames('section-countdown text-center', className)}>
              <Row className="d-flex align-items-center justify-content-center m-0">
                <Col xs="12">
                  <h3 className={classNames(`section-heading mb-3`, headingClass)}>
                    The June global screening event ends in:
                  </h3>
                </Col>
                <Col xs="12" className="timer">
                  <CountdownTimer time={launchEnd} />
                </Col>
              </Row>
            </div>
          </>
        ) : null}
      </>
    );
  }

  function DiscoverBox({image, heading, text, ...props}) {
    return (
      <Col lg="6" className="d-flex flex-column mb-4">
        <div className="background-white box-shadow-black-25 text-center d-flex flex-column flex-grow-1">
          <div
            className="discover-image background-cover background-image-center"
            style={{backgroundImage: `url(${image})`}}
          />
          <div className="p-4 d-flex flex-column flex-grow-1">
            <h4 className="section-heading mb-3">{heading}</h4>
            <p className="my-auto">{text}</p>
          </div>
        </div>
      </Col>
    );
  }

  useBalanceText();
  return (
    <div className={classNames(`page d-flex flex-column`, noShareButtons ? `page-nosocial` : null)}>
      <PageMeta />
      <div className="page-content">
        <SectionTriangle
          id="ff-landing-header-v1"
          color="black"
          className="section-triangle-first"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-header-backdrop-landon-parenteau-EEuDMqRYbx0-unsplash.jpg')}
          offset="bottom"
          innerClass="p-0"
        >
          <div
            className="background-ff-gray-80 text-shadow-dark text-white text-center pt-4 pb-3"
            style={{position: 'relative'}}
          >
            {phase === 'during' ? (
              <>
                <Timer headingClass="text-h4" className="countdown-small" />
              </>
            ) : (
              <h4 className="section-only-heading">Now Playing FREE for a Limited Time!</h4>
            )}
            <TriangleBottom color="ff-gray-80" />
          </div>
          <Container>
            <Row>
              <Col>
                <div className="optin-box background-ff-cream p-4 p-lg-5">
                  <Laurels noTomato />
                  <h4 className="text-center text-din text-400 mt-4 mb-3">Enter Your Email Now to Watch</h4>
                  <h2 className="text-h1 text-center mt-0 mb-1">
                    The <i>Fantastic Fungi</i>
                  </h2>
                  <h2 className="text-center mt-0 mb-3">
                    <i>Special Event Edition</i>
                  </h2>
                  <p className="balance-text text-center mb-4">(And, get our FREE RECIPE GUIDE just for joining us!)</p>
                  <FilmOptinForm linkColor="black" />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-trailer" color="white" type="both">
          <Container className="px-mobile-none">
            <Video
              url="https://www.youtube.com/watch?v=bWax2eIU73Y"
              label="FF - Film Trailer"
              thumbnail="https://cdn.foodrevolution.org/ff/videothumb/ff-videothumb-v4.gif"
              noButton={true}
            />
            <Row className="px-mobile">
              <Col>
                <h3 className="section-only-heading text-center mt-5">
                  {variant === 'ad' ? (
                    <>
                      A story of innovators and disruptors, chefs and foragers, mental health, and healing the planet.
                    </>
                  ) : (
                    <>
                      A story of innovators and disruptors, chefs and foragers, healing mental health, and healing the
                      planet.
                    </>
                  )}
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-landing-testimonials" color="ff-cream" type="bottom">
          <Container>
            <Row>
              <Col>
                <p className="text-center balance-text mb-4">
                  The <i>Fantastic Fungi</i> film has rocked audiences since it came out, with 100% ratings on Rotten
                  Tomatoes and rave reviews from film critics.
                </p>
              </Col>
            </Row>
            <Row className="d-flex align-items-stretch justify-content-center">
              {criticsLanding.map((critic, i) => (
                <Col md="6" className="d-flex flex-grow-1 text-center mb-4">
                  <div className="d-flex flex-column background-white box-shadow-black-25 p-4">
                    <div className="my-auto">
                      <ReactMarkdown source={critic.text} />
                      {critic.image ? (
                        <img src={critic.image} style={{maxHeight: '50px'}} />
                      ) : (
                        <p className="mb-0">— {critic.name}</p>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-recipes" color="white" type="bottom">
          <Container>
            <Row className="align-items-center justify-content-center">
              <Col lg="6" className="text-center text-lg-left mb-4 mb-lg-0">
                <p className="balance-text text-h4 mb-3">
                  In celebration of this special screening event, get a curated recipe guide with 20 of our favorite
                  whole foods plant-based mushroom dishes from
                </p>
                <img src="https://cdn.foodrevolution.org/logo/frn-logo.svg" style={{maxHeight: '75px'}} />
              </Col>
              <Col md="10" lg="6">
                <img className="" src="https://cdn.foodrevolution.org/ff/mouthwatering-mushrooms-cover.png" />
              </Col>
            </Row>
            <Row className="mt-5 text-center">
              <Col>
                <FilmOptinModalTrigger>Get The Free Recipe Guide</FilmOptinModalTrigger>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-watch"
          color="black"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-body-voices-marc-markstein-ibouKITXBFM-unsplash.jpg')}
          innerClass="d-flex flex-grow-1 text-shadow-dark text-white text-center"
          offset="bottom"
          className="background-overlay-black-70"
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg="10">
                <h3 className="section-only-heading">
                  {
                    {
                      default: (
                        <>
                          As you watch this special event edition of <i>Fantastic Fungi</i> film, you’ll get to witness
                          an incredibly moving and inspiring story — a story of evolving consciousness, healing, and
                          connection.
                        </>
                      ),
                      ad: (
                        <>
                          There’s life <i>before</i> watching the special event edition of <i>Fantastic Fungi…</i> And
                          life <i>after.</i>
                        </>
                      )
                    }[variant]
                  }
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="ff-" color="white" type="both" className="text-center">
          <Container>
            <Row>
              <Col>
                {variant === 'ad' ? (
                  <>
                    <p className="balance-text">
                      Be prepared for a thrilling ride that includes gorgeous timelapse photography to give you chills,
                      instill you with a newfound sense of wonder, and fill you with hope for the future.
                    </p>
                    <p className="balance-text">
                      Fungi are the largest, most powerful organism on Earth. They’ve also been the most
                      underappreciated — until now.
                    </p>
                    <p className="balance-text">
                      Thanks to the critically acclaimed film, <i>Fantastic Fungi,</i> you’re invited into a fascinating
                      realm that’s been literally hidden underground for most of human existence.
                    </p>
                    <h5 className="mt-0">
                      As you watch the special event edition of <i>Fantastic Fungi</i> film, you’ll get to witness an
                      incredibly moving and inspiring story — a story of evolving consciousness and connection.
                    </h5>
                    <p className="balance-text">Fungi have the power to steer our future in the right direction.</p>
                    <p className="balance-text">
                      But unless we learn how to understand the language of fungi, not only will we destroy these
                      organisms, but we will destroy ourselves.
                    </p>
                    <p className="balance-text">
                      This film is a <b>MUST-SEE</b> for anyone who cares about the fate of our planet.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="balance-text">Fungi have the power to steer our future in the right direction.</p>
                    <p className="balance-text">
                      But for years, scientists were barred from studying certain kinds of mushrooms. Now, new research
                      shows promise for depression, anxiety, PTSD, and more.
                    </p>
                    <p className="balance-text">
                      Unless we learn how to understand the language of fungi, not only will we destroy these organisms,
                      but we will destroy ourselves.
                    </p>
                    <p className="balance-text">
                      This film is a <b>MUST-SEE</b> for anyone who cares about the fate of humanity, and of our planet.
                    </p>
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="ff-landing-cta"
          color="ff-gray"
          imgUrl={require('static/backgrounds/ff-saleslanding-image-body-foodie-forfeathering-thanh-soledas-Kf1nBG76Xe8-unsplash-wide.jpg')}
          className="text-white text-center text-shadow-dark background-overlay-ff-gray-90"
        >
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-din mb-4">Don’t Miss Your Chance!</h3>
                <h4 className="text-din text-400 mt-0 mb-3">For a limited time watch the</h4>
                <h2 className="text-h1 mt-0 mb-5">
                  <i>Fantastic Fungi Special Event Edition</i>
                </h2>
                <FilmOptinModalTrigger />
                <Timer className="mt-4" />
              </Col>
            </Row>
          </Container>
        </SectionTriangle>
      </div>

      {noExitIntent ? null : <ModalExitLanding />}

      <div className="footer-content mt-auto">
        <Footer partner />
        {noShareButtons ? null : <ShareButtons floating />}
      </div>
      <FilmOptinModal />
    </div>
  );
}
